/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { __ } from '@web-stories-wp/i18n';
import { useEffect, useMemo, useState } from '@web-stories-wp/react';
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  DropDown,
  Icons,
  NumericInput,
  Text,
  THEME_CONSTANTS,
  ToggleButton,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { useStory } from '../../../../app';
import { getLayerName } from '../../../../elements';
import {
  getTextBoxAttachmentValidationError,
  getTextBoxAttachmentPosition,
  isTextBoxAttachment,
  isTextBoxAttachmentDependent,
} from '../../../../utils/textBoxAttachment';
import Tooltip from '../../../tooltip';
import { SimplePanel } from '../../panel';
import { focusStyle, inputContainerStyleOverride } from '../../shared';

const Controls = styled.div`
  display: grid;
  min-width: 0;
  gap: 16px;
`;

const selectButtonStylesOverride = css`
  min-width: 0;
  max-width: 100%;
`;

const selectValueStylesOverride = css`
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
`;

const menuStylesOverride = css`
  max-width: 100%;

  li,
  li > span {
    min-width: 0;
  }

  li > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const FullWidthDropDown = styled(DropDown).attrs({
  selectButtonStylesOverride,
  selectValueStylesOverride,
})`
  width: 100%;
  min-width: 0;
  max-width: 100%;
`;

const AlignmentGapRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AlignmentControls = styled.div`
  display: flex;
  flex: 0 0 auto;
  gap: 4px;
`;

const GapInput = styled(NumericInput)`
  flex: 1 1 0;
  width: auto;
  min-width: 0;
`;

const AlignmentButton = styled(ToggleButton).attrs({
  size: BUTTON_SIZES.SMALL,
  variant: BUTTON_VARIANTS.SQUARE,
})`
  ${focusStyle};
`;

const Hint = styled(Text)`
  color: ${({ theme }) => theme.colors.fg.secondary};
`;

const NOT_PINNED_TARGET = 'not-pinned';
const CUSTOM_ALIGNMENT_EPSILON = 0.01;

function getUniqueLayerOptions(elements, getValidationError) {
  const seen = new Map();
  return elements.map((element) => {
    const baseLabel =
      String(getLayerName(element) || '').trim() ||
      __('Untitled layer', 'web-stories');
    const count = (seen.get(baseLabel) || 0) + 1;
    seen.set(baseLabel, count);
    const uniqueLabel = count === 1 ? baseLabel : `${baseLabel} (${count})`;
    return {
      label: uniqueLabel,
      value: element.id,
      disabled: Boolean(getValidationError(element.id)),
    };
  });
}

function TextPinningPanel({ selectedElements }) {
  const dependent = selectedElements[0];
  const { currentPage, setTextBoxAttachment } = useStory(
    ({ state, actions }) => ({
      currentPage: state.currentPage,
      setTextBoxAttachment: actions.setTextBoxAttachment,
    })
  );
  const attachment = isTextBoxAttachment(dependent?.textBoxAttachment)
    ? dependent.textBoxAttachment
    : null;
  const position = getTextBoxAttachmentPosition(attachment);
  const [gap, setGap] = useState(attachment?.gap ?? 0);
  const hasCustomAlignment =
    Boolean(attachment) &&
    Math.abs(attachment.crossAxisOffset) > CUSTOM_ALIGNMENT_EPSILON;

  const targetOptions = useMemo(() => {
    if (!isTextBoxAttachmentDependent(dependent)) {
      return [];
    }
    const elements = currentPage?.elements || [];
    return [
      { label: __('Not pinned', 'web-stories'), value: NOT_PINNED_TARGET },
      ...getUniqueLayerOptions([...elements].reverse(), (targetId) =>
        getTextBoxAttachmentValidationError(elements, dependent.id, targetId)
      ),
    ];
  }, [currentPage?.elements, dependent]);
  const selectedTargetValue = attachment?.targetId || NOT_PINNED_TARGET;
  const selectedTargetLabel = targetOptions.find(
    ({ value }) => value === selectedTargetValue
  )?.label;

  useEffect(() => {
    setGap(attachment?.gap ?? 0);
  }, [attachment?.gap, dependent?.id]);

  if (!isTextBoxAttachmentDependent(dependent)) {
    return null;
  }

  const isReadOnly = Boolean(currentPage?.readonly || dependent.isLocked);
  const isGroupLocked = Boolean(
    dependent.groupId && currentPage?.groups?.[dependent.groupId]?.isLocked
  );

  const handleTargetChange = (nextTargetId) => {
    if (nextTargetId === NOT_PINNED_TARGET) {
      setTextBoxAttachment({
        elementId: dependent.id,
        targetId: null,
      });
      return;
    }

    const elements = currentPage?.elements || [];
    const target = elements.find(({ id }) => id === nextTargetId);
    if (
      !target ||
      getTextBoxAttachmentValidationError(elements, dependent.id, nextTargetId)
    ) {
      return;
    }

    // Derive the complete relationship once, from the reducer's current boxes.
    setTextBoxAttachment({
      elementId: dependent.id,
      targetId: nextTargetId,
    });
  };

  const handlePositionChange = (nextPosition) => {
    if (!attachment || nextPosition === position) {
      return;
    }
    // Rebase both offsets from current boxes without alignment snap intent.
    setTextBoxAttachment({
      elementId: dependent.id,
      targetId: attachment.targetId,
      position: nextPosition,
    });
  };

  const handleAlignmentChange = (nextAlignment) => {
    if (!attachment) {
      return;
    }
    setTextBoxAttachment({
      elementId: dependent.id,
      targetId: attachment.targetId,
      alignment: nextAlignment,
      gap: attachment.gap,
    });
  };

  const normalizeGap = (value) => {
    return Number.isFinite(value) ? value : 0;
  };

  const handleGapChange = (value) => {
    setGap(normalizeGap(value));
  };

  const handleGapCommit = (value) => {
    const nextGap = normalizeGap(value);
    setGap(nextGap);
    if (!attachment || nextGap === attachment.gap) {
      return;
    }
    setTextBoxAttachment({
      elementId: dependent.id,
      targetId: attachment.targetId,
      gap: nextGap,
    });
  };

  return (
    <SimplePanel name="textPinning" title={__('Pin to Layer', 'web-stories')}>
      <Controls>
        <Hint size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
          {__(
            'Pin to keep this layer positioned relative to another layer.',
            'web-stories'
          )}
        </Hint>
        <FullWidthDropDown
          ariaLabel={__('Layer to pin to', 'web-stories')}
          disabled={isReadOnly || isGroupLocked}
          dropDownLabel={__('Layer', 'web-stories')}
          menuStylesOverride={menuStylesOverride}
          options={targetOptions}
          selectedValue={selectedTargetValue}
          title={selectedTargetLabel}
          onMenuItemClick={(event, value) => handleTargetChange(value)}
        />
        {attachment && (
          <FullWidthDropDown
            ariaLabel={__('Pin position', 'web-stories')}
            disabled={isReadOnly || isGroupLocked}
            dropDownLabel={__('Position', 'web-stories')}
            options={[
              { label: __('Above', 'web-stories'), value: 'above' },
              { label: __('Below', 'web-stories'), value: 'below' },
            ]}
            selectedValue={position}
            onMenuItemClick={(event, value) => handlePositionChange(value)}
          />
        )}
        {attachment && (
          <Hint size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
            {position === 'below'
              ? __("Follow the target layer's bottom edge.", 'web-stories')
              : __("Follow the target layer's top edge.", 'web-stories')}
          </Hint>
        )}
        {attachment && (
          <AlignmentGapRow>
            <AlignmentControls
              role="group"
              aria-label={__('Pin alignment', 'web-stories')}
            >
              <Tooltip title={__('Align left', 'web-stories')}>
                <AlignmentButton
                  aria-label={__('Align pin left', 'web-stories')}
                  disabled={isReadOnly || isGroupLocked}
                  isToggled={
                    !hasCustomAlignment && attachment.alignment === 'start'
                  }
                  onClick={() => handleAlignmentChange('start')}
                >
                  <Icons.AlignLeft />
                </AlignmentButton>
              </Tooltip>
              <Tooltip title={__('Align center', 'web-stories')}>
                <AlignmentButton
                  aria-label={__('Align pin center', 'web-stories')}
                  disabled={isReadOnly || isGroupLocked}
                  isToggled={
                    !hasCustomAlignment && attachment.alignment === 'center'
                  }
                  onClick={() => handleAlignmentChange('center')}
                >
                  <Icons.AlignCenter />
                </AlignmentButton>
              </Tooltip>
              <Tooltip title={__('Align right', 'web-stories')}>
                <AlignmentButton
                  aria-label={__('Align pin right', 'web-stories')}
                  disabled={isReadOnly || isGroupLocked}
                  isToggled={
                    !hasCustomAlignment && attachment.alignment === 'end'
                  }
                  onClick={() => handleAlignmentChange('end')}
                >
                  <Icons.AlignRight />
                </AlignmentButton>
              </Tooltip>
            </AlignmentControls>
            <GapInput
              aria-label={__('Gap between pinned layers', 'web-stories')}
              canBeNegative
              containerStyleOverride={inputContainerStyleOverride}
              disabled={isReadOnly || isGroupLocked}
              isFloat
              onBlur={(event, value) => handleGapCommit(value)}
              onChange={(event, value) => handleGapChange(value)}
              suffix={__('Gap', 'web-stories')}
              value={gap}
            />
          </AlignmentGapRow>
        )}
        {attachment && (
          <Hint size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
            {__(
              'Use a negative gap to overlap the pinned layers.',
              'web-stories'
            )}
          </Hint>
        )}
      </Controls>
    </SimplePanel>
  );
}

TextPinningPanel.propTypes = {
  selectedElements: PropTypes.array.isRequired,
};

export default TextPinningPanel;
